import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { ProfilePageWrapper } from 'components/layout/ProfilPageWrapper';
import Layout from 'components/layout/Layout';
import { Form, Formik, FormikConfig, FormikValues } from 'formik';
import { validateSchema } from '@inspire/data/helpers/schemas';
import { handleErrors } from 'lib/errors';
import { useSetPhoneNumberMutation } from 'lib/generated';
import {
  pushEvent,
  GTM_CATEGORY_ECLAIREURS,
  GTM_ACTION_ADD_NUMBER,
  GTM_CATEGORY_COMPTE,
  GTM_ACTION_ADD_NUMBER_ECLAIREUR,
} from 'lib/gtm';
import { useUser, useMyProfile, useSetMyProfile } from 'lib/hooks';
import { successToast } from 'lib/toasts';
import { getSession } from 'next-auth/client';
import { useState } from 'react';
import { FormStack } from '@inspire/ui/chakra/forms/Form';
import StandaloneCheckbox from '@inspire/ui/chakra/forms/StandaloneCheckbox';
import { EditIcon } from '@inspire/ui/icons';
import { InputControl } from 'formik-chakra-ui';
import { phoneNumberField } from '@inspire/data/schemas/user/phone-number';
import Span from '@inspire/ui/chakra/Span';
import { OnlyLoggedIn } from 'components/auth/Redirects';

const PreferencesPage = () => {
  const { isEclaireur, isProf } = useUser();
  const { myProfile, refetchMyProfile, isRefetching } = useMyProfile();
  const { setMyProfileAsync } = useSetMyProfile();
  const [isUpdatingNumber, setIsUpdatingNumber] = useState(false);
  const { mutateAsync: setPhoneNumberAsync } = useSetPhoneNumberMutation();
  const schema = phoneNumberField.pick('number', 'verifyToken');

  const formik: FormikConfig<FormikValues> = {
    initialValues: {
      number: myProfile?.phoneNumber?.number || '',
      verifyToken: '',
    },
    validate: (val) => validateSchema(schema, val),
    onSubmit: async (val) => {
      try {
        await setPhoneNumberAsync({
          numberToVerify: val.number,
          tokenToVerify: val.verifyToken,
        });
        await getSession();
        await refetchMyProfile();
        if (val.verifyToken) {
          successToast('Numéro de téléphone validé');
          pushEvent(
            GTM_CATEGORY_ECLAIREURS,
            GTM_ACTION_ADD_NUMBER,
            'Validation numéro de tel'
          );
        } else {
          successToast('Tu vas recevoir un SMS avec ton code de confirmation.');
          pushEvent(
            GTM_CATEGORY_ECLAIREURS,
            GTM_ACTION_ADD_NUMBER,
            'Confirmation code numéro de tel'
          );
        }
      } catch ({ errors }) {
        handleErrors(errors);
        setIsUpdatingNumber(false);
      }
    },
  };

  return (
    <>
      {!isProf && (
        <Checkbox isChecked isDisabled mb={2}>
          <Span ml={1}>
            Je souhaite être notifié par mail des nouveaux messages que je
            reçois sur INSPIRE (nécessaire au bon fonctionnement du service)
          </Span>
        </Checkbox>
      )}

      <StandaloneCheckbox
        label={
          isProf
            ? "Je souhaite recevoir des communications périodiques de l'équipe INSPIRE (conseils pour optimiser l’utilisation de la plateforme en classe, ressources utiles, invitations à des webinaires avec notre communauté de profs, etc…)"
            : isEclaireur
            ? 'Je veux recevoir par email des actualités sur l’insertion professionnelle, la vie étudiante, les ateliers auprès de lycéens et les évènements avec d’autres étudiants.'
            : "Je veux recevoir par email des conseils sur l'orientation post-bac, la vie étudiante et le monde du travail."
        }
        isChecked={myProfile.newsletter}
        onChange={async () => {
          try {
            await setMyProfileAsync({
              newsletter: !myProfile.newsletter,
              update_newsletter: new Date(),
            });
            await getSession();
            await refetchMyProfile();
          } catch ({ errors }) {
            handleErrors(errors);
          }
        }}
      />

      {isEclaireur && (
        <Box mt={8}>
          <Heading size="md">Option SMS</Heading>

          <StandaloneCheckbox
            mb={4}
            label="En plus des emails, je souhaite recevoir un SMS lorsqu'un nouveau lycéen me contacte pour la première fois."
            isChecked={myProfile?.notifications?.media?.sms}
            onChange={async () => {
              try {
                await setMyProfileAsync({
                  notifications: {
                    media: {
                      ...myProfile?.notifications?.media,
                      sms: !myProfile?.notifications?.media?.sms,
                    },
                  },
                });
                await getSession();
                await refetchMyProfile();
                pushEvent(
                  GTM_CATEGORY_COMPTE,
                  GTM_ACTION_ADD_NUMBER_ECLAIREUR,
                  'clic option SMS'
                );
              } catch ({ errors }) {
                handleErrors(errors);
              }
            }}
          />

          {/* {myProfile?.notifications?.media?.sms && (
            <Text color="GrayText" fontStyle="italic" pl={7}>
              L'enregistrement de numéro de téléphone sera à nouveau bientôt
              disponible.
            </Text>
          )} */}

          {myProfile?.notifications?.media?.sms && (
            <>
              {!myProfile?.phoneNumber?.verified || isUpdatingNumber ? (
                <Formik {...formik}>
                  {({ isSubmitting }) => (
                    <Form>
                      <FormStack spacing={2} maxW={350}>
                        <InputControl
                          name="number"
                          label="Numéro de téléphone"
                          inputProps={{ placeholder: 'XX XX XX XX XX' }}
                          isRequired
                        />
                        {myProfile.phoneNumber?.number &&
                          !myProfile?.phoneNumber?.verified && (
                            <InputControl
                              name="verifyToken"
                              label="Code de confirmation"
                            />
                          )}
                        <Flex>
                          <Button
                            type="submit"
                            isLoading={isRefetching}
                            disabled={isSubmitting}
                          >
                            Valider
                          </Button>
                          {isUpdatingNumber && (
                            <Button
                              ml={2}
                              onClick={() => setIsUpdatingNumber(false)}
                            >
                              Annuler
                            </Button>
                          )}
                        </Flex>
                      </FormStack>
                    </Form>
                  )}
                </Formik>
              ) : (
                <FormControl>
                  <FormLabel htmlFor="phoneNumber">
                    Numéro de téléphone
                  </FormLabel>
                  <Flex alignItems="center">
                    <Text id="phoneNumber" m={0}>
                      {myProfile?.phoneNumber?.number}
                    </Text>
                    <IconButton
                      aria-label="Modifier"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUpdatingNumber(true);
                      }}
                      variant="ghost"
                      icon={<Icon as={EditIcon} />}
                      ml={2}
                    />
                  </Flex>
                </FormControl>
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

PreferencesPage.getWrappers = (page) => (
  <Layout variant="adaptative" rowVariant="gray" containerWidth="lg">
    <OnlyLoggedIn>
      <ProfilePageWrapper>{page}</ProfilePageWrapper>
    </OnlyLoggedIn>
  </Layout>
);

export default PreferencesPage;
